import { CancelButton, EditButton } from '@core/components/Buttons';
import { CustomExpandedPanel } from '@core/components/CustomExpandedPanel';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { TextLabel } from '@core/components/Inputs';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Divider, Grid, Typography } from '@material-ui/core';
import ServicoIcon from 'app/assets/icon/ServicoIcon';
import { calcularValorComDesconto } from 'app/utils/monetarioUtil';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { ServicoContext } from '../../../ServicoContext';
import { TipoExigibilidade } from '../enums';
import { servicoModais } from '../servicoModais';

const getCurrency = (value) => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const valorFormatado = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  const retirarCurrency = valorFormatado.replace('R$', '');
  const valorSemEspacoEmBranco = retirarCurrency.slice(1);
  return valorSemEspacoEmBranco;
};

const VisualizarServicoModal = () => {
  const [modal, setModal] = useContext(ServicoContext).modal;
  const [servico] = useContext(ServicoContext).servicoAtual;

  // FA-08 Fechar
  const handleCloseModal = () => {
    setModal(false);
  };

  const handleOpenEditMode = () => {
    setModal(servicoModais.editarServicoModal);
  };

  const emPromocao =
    servico.promocao &&
    moment()
      .startOf('day')
      .isBetween(servico.promocao.vigenciaInicio, servico.promocao.vigenciaFim, undefined, '[]');

  const valorComDesconto = calcularValorComDesconto(
    servico?.promocao?.tipo,
    servico?.valorVenda,
    servico?.promocao?.valor
  );

  let periodoPromocao = '';

  if (servico?.promocao?.vigenciaInicio && servico?.promocao?.vigenciaFim) {
    periodoPromocao = `${moment(servico?.promocao?.vigenciaInicio).format('DD/MM/YYYY')} a ${moment(
      servico?.promocao?.vigenciaFim
    ).format('DD/MM/YYYY')}`;
  }

  const getMonetarioFormatado = (tipo, valor) => {
    const decimal = getCurrency(valor);

    if (tipo === 'VALOR') {
      return `R$ ${decimal}`;
    }
    if (tipo === 'PERCENTUAL') {
      return `${decimal}`;
    }
  };

  const formatCodigoFiscal = (item) => {
    if (!item) {
      return '';
    }
    if (item.descricao) {
      return `${item.codigo} - ${item.descricao}`;
    }
    if (item.codigo) {
      return item.codigo;
    }
    return '';
  };

  const possuiValorMonetario = (valor) => valor !== null && valor !== undefined;

  const getValorMonetarioExibicao = (tipo, valor) => {
    if (!possuiValorMonetario(valor)) {
      return '-';
    }

    return getMonetarioFormatado(tipo, valor);
  };

  const getLabelImposto = (nome, tipo) => (tipo === 'VALOR' ? `${nome} (R$)` : `${nome} (%)`);

  const getPromocaoTipo = () => {
    if (servico?.promocao?.tipo === 'PERCENTUAL') {
      return '(%)';
    }
    return '(R$)';
  };

  const DadosServico = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel id="descricao" label="Nome do serviço prestado" value={servico?.descricao} />
        </Grid>
        <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
          <TextLabel id="codigo" label="Código de identificação" value={servico?.codigo} />
        </Grid>
        <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
          <TextLabel id="valorCusto" label="Valor de Custo (R$)" value={getCurrency(servico?.valorCusto)} />
        </Grid>
        <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
          <TextLabel id="valorVenda" label="Valor de Venda (R$)" value={getCurrency(servico?.valorVenda)} />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="descricaoDetalhada"
            label="Descrição detalhada do serviço"
            value={(servico?.descricaoDetalhada || '').split('\n')}
          />
        </Grid>
        {emPromocao && (
          <Grid item container spacing={3}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography variant="h6">Serviço em promoção</Typography>
              <Divider />
            </Grid>
            <hr />
            <Grid item xs={4} sm={4} md={4} lg={4} xl={4} alignContent="center">
              <TextLabel id="vigencia" label="Período de vigência" value={periodoPromocao} />
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
              <TextLabel
                id="desconto"
                label={`Desconto ${getPromocaoTipo()}`}
                value={
                  servico?.promocao?.tipo === 'PERCENTUAL'
                    ? servico?.promocao?.valor.toFixed(2)
                    : getCurrency(servico?.promocao?.valor)
                }
              />
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
              <TextLabel
                id="valorComDesconto"
                label="Valor com desconto (R$)"
                value={getCurrency(valorComDesconto) ? `${getCurrency(valorComDesconto)}` : ''}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const DadosFiscais = () => {
    const lc116 = servico?.fiscal?.lc116;
    const cnae = servico?.fiscal?.cnae;
    const codigoTributacaoNacional = servico?.fiscal?.codigoTributacaoNacional;
    const nbs = servico?.fiscal?.nbs;
    const cstSituacaoTributaria = servico?.fiscal?.cstSituacaoTributaria;
    const cstClassificacaoTributaria = servico?.fiscal?.cstClassificacaoTributaria;

    const exibirSecaoIbsCbs =
      servico?.fiscal?.possuiIbsCbs ||
      cstSituacaoTributaria?.id ||
      cstSituacaoTributaria?.codigo ||
      cstClassificacaoTributaria?.id ||
      cstClassificacaoTributaria?.codigo ||
      possuiValorMonetario(servico?.fiscal?.valorIbs) ||
      possuiValorMonetario(servico?.fiscal?.valorIbsMunicipal) ||
      possuiValorMonetario(servico?.fiscal?.valorCbs) ||
      possuiValorMonetario(servico?.fiscal?.valorIs);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="cnae"
            label="CNAE do serviço"
            value={cnae?.descricao ? `${cnae.codigo} - ${cnae.descricao}` : ''}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="codigoServicoMunicipal"
            label="Código de serviço municipal"
            value={servico?.fiscal?.codigoServicoMunicipal}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="codigoLeiComplementar116"
            label="Código da Lei Complementar 116"
            value={lc116?.descricao ? `${lc116.codigo} - ${lc116.descricao}` : ''}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="codigoTributacaoNacional"
            label="Código Tributação Nacional - CTN"
            value={
              codigoTributacaoNacional?.descricao
                ? `${codigoTributacaoNacional.codigo} - ${codigoTributacaoNacional.descricao}`
                : ''
            }
          />
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="naturezaOperacao"
            label="Natureza da operação"
            value={TipoExigibilidade[servico?.naturezaOperacao]}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <TextLabel
            id="nbs"
            label="Nomenclatura brasileira de serviços (NBS)"
            value={nbs?.descricao ? `${nbs.codigo} - ${nbs.descricao}` : ''}
          />
        </Grid>

        <Grid item xs={12}>
          <TextLabel
            id="valorIss"
            label={getLabelImposto('ISS', servico?.fiscal?.tipoIss)}
            value={getValorMonetarioExibicao(servico?.fiscal?.tipoIss, servico?.fiscal?.valorIss)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextLabel
            id="valorIrrf"
            label={getLabelImposto('IRRF', servico?.fiscal?.tipoIrrf)}
            value={getValorMonetarioExibicao(servico?.fiscal?.tipoIrrf, servico?.fiscal?.valorIrrf)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextLabel
            id="valorPis"
            label={getLabelImposto('PIS', servico?.fiscal?.tipoPis)}
            value={getValorMonetarioExibicao(servico?.fiscal?.tipoPis, servico?.fiscal?.valorPis)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextLabel
            id="valorCofins"
            label={getLabelImposto('COFINS', servico?.fiscal?.tipoCofins)}
            value={getValorMonetarioExibicao(servico?.fiscal?.tipoCofins, servico?.fiscal?.valorCofins)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextLabel
            id="valorInss"
            label={getLabelImposto('INSS', servico?.fiscal?.tipoInss)}
            value={getValorMonetarioExibicao(servico?.fiscal?.tipoInss, servico?.fiscal?.valorInss)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextLabel
            id="valorCsll"
            label={getLabelImposto('CSLL', servico?.fiscal?.tipoCsll)}
            value={getValorMonetarioExibicao(servico?.fiscal?.tipoCsll, servico?.fiscal?.valorCsll)}
          />
        </Grid>

        {exibirSecaoIbsCbs && (
          <>
            <Grid item xs={12}>
              <Divider />
              <Typography variant="body1" style={{ fontWeight: 'bold', marginTop: 8 }}>
                IBS / CBS - Reforma Tributária
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextLabel
                id="cstSituacaoTributaria"
                label="Código de situação tributária (CST)"
                value={formatCodigoFiscal(cstSituacaoTributaria)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextLabel
                id="cstClassificacaoTributaria"
                label="Código de classificação tributária"
                value={formatCodigoFiscal(cstClassificacaoTributaria)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextLabel
                id="valorIbs"
                label={servico?.fiscal?.tipoIbs === 'VALOR' ? 'IBS Estadual (R$)' : 'IBS Estadual (%)'}
                value={getValorMonetarioExibicao(servico?.fiscal?.tipoIbs, servico?.fiscal?.valorIbs)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextLabel
                id="valorIbsMunicipal"
                label={
                  servico?.fiscal?.tipoIbsMunicipal === 'VALOR' ? 'IBS Municipal (R$)' : 'IBS Municipal (%)'
                }
                value={getValorMonetarioExibicao(
                  servico?.fiscal?.tipoIbsMunicipal,
                  servico?.fiscal?.valorIbsMunicipal
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <TextLabel
                id="valorCbs"
                label={servico?.fiscal?.tipoCbs === 'VALOR' ? 'CBS (R$)' : 'CBS (%)'}
                value={getValorMonetarioExibicao(servico?.fiscal?.tipoCbs, servico?.fiscal?.valorCbs)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextLabel
                id="valorIs"
                label={servico?.fiscal?.tipoIs === 'VALOR' ? 'IS (R$)' : 'IS (%)'}
                value={getValorMonetarioExibicao(servico?.fiscal?.tipoIs, servico?.fiscal?.valorIs)}
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const Content = () => {
    const [expanded, setExpanded] = useState('dadosServico');

    const handleExpandedChange = (key, value) => {
      if (value) {
        setExpanded(key);
      } else {
        setExpanded(false);
      }
    };
    return (
      <div>
        <CustomExpandedPanel
          title="Dados do serviço"
          expanded={expanded === 'dadosServico'}
          defaultExpanded
          onChange={(_, value) => handleExpandedChange('dadosServico', value)}
        >
          <DadosServico />
        </CustomExpandedPanel>
        <CustomExpandedPanel
          title="Dados fiscais"
          expanded={expanded === 'dadosFiscais'}
          onChange={(_, value) => handleExpandedChange('dadosFiscais', value)}
        >
          <DadosFiscais />
        </CustomExpandedPanel>
      </div>
    );
  };

  return (
    <SimpleDialog maxWidth="660" open={modal === servicoModais.visualizarServicoModal} onClose={handleCloseModal}>
      <SimpleDialogContent
        header={{
          title: 'Cadastro de serviço',
          icon: <ServicoIcon />,
          toolbar: (
            <>
              <CancelButton onClick={handleCloseModal}>Fechar</CancelButton>
              {permissaoUtils.servico().editar && (
                <EditButton id="add-classe-root" type="submit" onClick={handleOpenEditMode}>
                  Editar
                </EditButton>
              )}
            </>
          ),
        }}
        contentStyle={{ padding: 0 }}
        content={<Content />}
      />
    </SimpleDialog>
  );
};

export default VisualizarServicoModal;
